import { useContext, useEffect, useMemo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getMyGroups, getGroupByIdWithMembers } from '_redux/modules/group';
import { GROUP_IS_ADMIN, GROUP_IS_HOST } from '_utils/constant';
import { SelectIdGroupContext } from '_context/SelectGroupContext';
import { ROLE_TYPES } from '../constant';

export default function useMembersData() {
  const dispatch = useDispatch();
  const { infoGroupSelected } = useContext(SelectIdGroupContext);
  const { myGroups } = useSelector((state) => state.group);
  const [loading, setLoading] = useState(false);
  const groupId = infoGroupSelected?.groupId;

  const groupInfo = useMemo(() => {
    if (!myGroups || !groupId) return null;
    return myGroups.find(
      (group) => group.gid === groupId || group.id === groupId
    );
  }, [myGroups, groupId]);

  const fetchMembers = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      if (!myGroups || myGroups.length === 0) {
        await dispatch(getMyGroups);
      }
      await dispatch(getGroupByIdWithMembers({ id: groupId }));
    } finally {
      setLoading(false);
    }
  }, [dispatch, groupId]);

  useEffect(() => {
    if (groupId) {
      fetchMembers();
    }
  }, [groupId, fetchMembers]);

  const [localOverrides, setLocalOverrides] = useState({});

  const updateMemberStatus = useCallback((userId, fields) => {
    setLocalOverrides((prev) => ({
      ...prev,
      [String(userId)]: {
        ...(prev[String(userId)] || {}),
        ...fields,
      },
    }));
  }, []);

  const members = useMemo(() => {
    if (!groupInfo?.members) return [];

    return groupInfo.members.map((item, index) => {
      const override = localOverrides[String(item?.uid || item?.id)];
      const currentItem = override ? { ...item, ...override } : item;

      let role = ROLE_TYPES.MEMBER;
      if (
        currentItem?.isAdmin === GROUP_IS_ADMIN &&
        currentItem?.isHost === GROUP_IS_HOST
      ) {
        role = ROLE_TYPES.ADMIN;
      } else if (currentItem?.isHost === GROUP_IS_HOST) {
        role = ROLE_TYPES.HOST;
      }

      return {
        ...currentItem,
        no: index + 1,
        role,
        totalEvents: currentItem?.totalEvents || 0,
        orderCount: currentItem?.orderCount || 0,
      };
    });
  }, [groupInfo?.members, localOverrides]);

  const stats = useMemo(() => {
    const total = members?.length || 0;
    const admins = members?.filter((m) => m?.role === ROLE_TYPES.ADMIN)?.length || 0;
    const regularMembers = members?.filter((m) => m?.role === ROLE_TYPES.MEMBER)?.length || 0;
    const banned = members?.filter((m) => Boolean(m?.isBanned || m?.banned))?.length || 0;
    const totalPurchases = members?.reduce(
      (sum, m) => sum + (Number(m?.orderCount) || 0),
      0
    ) || 0;

    return [
      { key: 'total', label: 'Total Members', value: total },
      { key: 'admins', label: 'Admins', value: admins },
      { key: 'members', label: 'Members', value: regularMembers },
      { key: 'totalPurchases', label: 'Total Purchases', value: totalPurchases },
      { key: 'banned', label: 'Banned', value: banned },
    ];
  }, [members]);

  return {
    members,
    stats,
    loading,
    groupId,
    groupInfo,
    groupName: groupInfo?.name || '',
    updateMemberStatus,
    refetchMembers: fetchMembers,
  };
}
